/**
 * Multi-marketplace currency utilities
 *
 * Bu modul marketplace-aware currency formatting icin kullanilir.
 * Mevcut formatGBP() fonksiyonunu bozmaz, generic alternatif sunar.
 *
 * Kullanim:
 *   import { formatCurrency, getCurrencyCode } from '../utils/currency';
 *   formatCurrency(12.99, 'GB')  // "£12.99"
 *   formatCurrency(12.99, 'US')  // "$12.99"
 *   formatCurrency(12.99, 'GBP') // "£12.99" (backward compat)
 */

export interface MarketplaceCurrencyConfig {
  code: string;
  symbol: string;
  locale: string;
  position: "before" | "after";
  spacing: boolean;
}

export const MARKETPLACE_CURRENCY: Record<string, MarketplaceCurrencyConfig> = {
  GB: {
    code: "GBP",
    symbol: "\u00a3",
    locale: "en-GB",
    position: "before",
    spacing: false,
  },
  US: {
    code: "USD",
    symbol: "$",
    locale: "en-US",
    position: "before",
    spacing: false,
  },
  DE: {
    code: "EUR",
    symbol: "\u20ac",
    locale: "de-DE",
    position: "after",
    spacing: true,
  },
  FR: {
    code: "EUR",
    symbol: "\u20ac",
    locale: "fr-FR",
    position: "after",
    spacing: true,
  },
  IT: {
    code: "EUR",
    symbol: "\u20ac",
    locale: "it-IT",
    position: "after",
    spacing: true,
  },
  ES: {
    code: "EUR",
    symbol: "\u20ac",
    locale: "es-ES",
    position: "after",
    spacing: true,
  },
  AU: {
    code: "AUD",
    symbol: "A$",
    locale: "en-AU",
    position: "before",
    spacing: false,
  },
  CA: {
    code: "CAD",
    symbol: "C$",
    locale: "en-CA",
    position: "before",
    spacing: false,
  },
};

/** eBay marketplace ID to Amazon marketplace code */
export const EBAY_MARKETPLACE_CURRENCY: Record<string, string> = {
  EBAY_GB: "GB",
  EBAY_US: "US",
  EBAY_DE: "DE",
  EBAY_FR: "FR",
  EBAY_IT: "IT",
  EBAY_ES: "ES",
  EBAY_AU: "AU",
  EBAY_CA: "CA",
};

/** Currency code to marketplace code (backward compat) */
const CURRENCY_TO_MARKETPLACE: Record<string, string> = {
  GBP: "GB",
  USD: "US",
  EUR: "DE",
  AUD: "AU",
  CAD: "CA",
};

/** Get currency config for marketplace. Falls back to GB. */
export function getCurrencyConfig(
  marketplace: string
): MarketplaceCurrencyConfig {
  return MARKETPLACE_CURRENCY[marketplace] || MARKETPLACE_CURRENCY.GB;
}

/** Get currency code (GBP, USD, EUR, etc.) */
export function getCurrencyCode(marketplace: string): string {
  return getCurrencyConfig(marketplace).code;
}

/** Get currency symbol */
export function getCurrencySymbol(marketplace: string): string {
  return getCurrencyConfig(marketplace).symbol;
}

/**
 * Format currency according to marketplace locale.
 *
 * Accepts both marketplace codes (GB, US) and currency codes (GBP, USD)
 * for backward compatibility.
 *
 * @example
 * formatCurrency(12.99, 'GB')  // "£12.99"
 * formatCurrency(12.99, 'US')  // "$12.99"
 * formatCurrency(12.99, 'DE')  // "12,99\u00a0€"
 * formatCurrency(12.99, 'GBP') // "£12.99" (backward compat)
 */
export function formatCurrency(
  value: any,
  marketplace: string = "GB"
): string {
  // Convert to number
  const num =
    typeof value === "number"
      ? value
      : parseFloat(String(value ?? "").replace(/[^0-9.\-]/g, "")) || 0;

  // Backward compatibility: accept currency codes
  const marketplaceCode =
    CURRENCY_TO_MARKETPLACE[marketplace] || marketplace;

  const config = getCurrencyConfig(marketplaceCode);

  return new Intl.NumberFormat(config.locale, {
    style: "currency",
    currency: config.code,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
}

/**
 * Format number without currency symbol, using marketplace locale.
 *
 * @example
 * formatCurrencyNumber(1234.56, 'GB') // "1,234.56"
 * formatCurrencyNumber(1234.56, 'DE') // "1.234,56"
 */
export function formatCurrencyNumber(
  value: any,
  marketplace: string = "GB"
): string {
  const num =
    typeof value === "number"
      ? value
      : parseFloat(String(value ?? "").replace(/[^0-9.\-]/g, "")) || 0;

  const config = getCurrencyConfig(marketplace);

  return new Intl.NumberFormat(config.locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
}

/**
 * Parse currency string to number.
 *
 * Handles both Anglo (1,234.56) and European (1.234,56) formats.
 *
 * @example
 * parseCurrency("£12.99")     // 12.99
 * parseCurrency("$1,234.56")  // 1234.56
 * parseCurrency("12,99 €")    // 12.99
 */
export function parseCurrency(value: string): number {
  if (!value) return 0;

  let cleaned = String(value).replace(/[^0-9.,]/g, "");

  if (cleaned.includes(",") && cleaned.includes(".")) {
    // Both present: determine which is decimal separator
    const lastComma = cleaned.lastIndexOf(",");
    const lastDot = cleaned.lastIndexOf(".");
    if (lastComma > lastDot) {
      // European: 1.234,56
      cleaned = cleaned.replace(/\./g, "").replace(",", ".");
    } else {
      // Anglo: 1,234.56
      cleaned = cleaned.replace(/,/g, "");
    }
  } else if (cleaned.includes(",")) {
    // Only comma: check if it's decimal (XX,YY) or thousands (X,XXX)
    if (/,\d{2}$/.test(cleaned)) {
      cleaned = cleaned.replace(",", ".");
    } else {
      cleaned = cleaned.replace(/,/g, "");
    }
  }

  return parseFloat(cleaned) || 0;
}

/** Convert eBay marketplace ID to marketplace code. Falls back to GB. */
export function getMarketplaceFromEbay(ebayMarketplaceId: string): string {
  return EBAY_MARKETPLACE_CURRENCY[ebayMarketplaceId] || "GB";
}

/** Get list of supported marketplace codes. */
export function getSupportedMarketplaces(): string[] {
  return Object.keys(MARKETPLACE_CURRENCY);
}

/** Check if marketplace code is supported. */
export function isMarketplaceSupported(marketplace: string): boolean {
  return marketplace in MARKETPLACE_CURRENCY;
}
